import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Avatar, 
  Tag, 
  Select, 
  Button, 
  Popconfirm,
  Empty,
} from 'antd';
import { 
  CrownOutlined, 
  UserOutlined, 
  PlusOutlined, 
  DeleteOutlined,
  TeamOutlined,
  RobotOutlined,
  DownOutlined,
  UpOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  IdcardOutlined,
  FileTextOutlined,
  LinkOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Modal, Divider, Descriptions, Image } from 'antd';
import { NodeDetailResponse, LifetreeDetailResponse, CharacterItem } from '../../api/models/Lifetree';
import { CharacterStateRow, NPCRow } from './types';

const { Text, Title } = Typography;
const { Option } = Select;

interface NodeCharactersTabProps {
  nodeDetail: NodeDetailResponse;
  lifetreeDetail: LifetreeDetailResponse | null;
  mainCharacter: CharacterItem | null;
  mainCharacterStateInNode: CharacterStateRow | null;
  npcRows: NPCRow[];
  onAddCharacterState: (characterId: string, stateId: string) => void;
  onRemoveCharacterState: (stateId: string) => void;
  onActivateState: (characterId: string, stateId: string) => void;
  onShowCreateCharacterModal: () => void;
  onSetSelectedCharacterForState: (charId: string) => void;
  onSetMethodSelectionModalOpen: (open: boolean) => void;
  onNPCCharacterChange: (rowId: string, characterId: string) => void;
  onNPCStateChange: (rowId: string, stateId: string) => void;
  onRemoveNPCRow: (rowId: string) => void;
  activatingStates: Set<string>;
  getAvailableNPCCharacters: (excludeRowId?: string) => CharacterItem[];
  getAvailableStatesForCharacter: (charId: string, includeStateId?: string | null) => any[];
  onPlanCharacters: () => Promise<void>;
  planningCharacters: boolean;
  onDeployCharacters?: () => Promise<void>;
  deployingCharacters?: boolean;
  onDeployCharactersFromPlan?: () => Promise<void>;
  deployingCharactersFromPlan?: boolean;
  onDeployStatesFromPlan?: () => Promise<void>;
  deployingStatesFromPlan?: boolean;
  onDeployRelationships?: () => Promise<void>;
  deployingRelationships?: boolean;
  onDeployImages?: () => Promise<void>;
  deployingImages?: boolean;
  onResetCharacters?: () => Promise<void>;
  resettingCharacters?: boolean;
  deployingFull?: boolean;
  deploymentProgress?: {
    total: number;
    current: number;
    status: string;
  } | null;
  direction?: string | null;
  plan?: any;
}

const NodeCharactersTab: React.FC<NodeCharactersTabProps> = ({
  nodeDetail,
  lifetreeDetail,
  mainCharacter,
  mainCharacterStateInNode,
  npcRows,
  onAddCharacterState,
  onRemoveCharacterState,
  onActivateState,
  onShowCreateCharacterModal,
  onSetSelectedCharacterForState,
  onSetMethodSelectionModalOpen,
  onNPCCharacterChange,
  onNPCStateChange,
  onRemoveNPCRow,
  activatingStates,
  getAvailableNPCCharacters,
  getAvailableStatesForCharacter,
  onPlanCharacters,
  planningCharacters,
  onDeployCharacters,
  deployingCharacters,
  onDeployCharactersFromPlan,
  deployingCharactersFromPlan,
  onDeployStatesFromPlan,
  deployingStatesFromPlan,
  onDeployRelationships,
  deployingRelationships,
  onDeployImages,
  deployingImages,
  onResetCharacters,
  resettingCharacters,
  deployingFull,
  deploymentProgress,
  direction,
  plan,
}) => {
  const [directionExpanded, setDirectionExpanded] = useState(false);
  const [relationshipModalVisible, setRelationshipModalVisible] = useState(false);
  const [viewPlanModalOpen, setViewPlanModalOpen] = useState(false);
  const [selectedNPCForRelationship, setSelectedNPCForRelationship] = useState<{
    character: CharacterItem;
    state: any;
  } | null>(null);
  const [stateDetailModal, setStateDetailModal] = useState<{
    visible: boolean;
    characterName: string;
    state: any | null;
  }>({ visible: false, characterName: '', state: null });

  const showRelationshipModal = (character: CharacterItem, state: any) => {
    setSelectedNPCForRelationship({ character, state });
    setRelationshipModalVisible(true);
  };

  const handleCharacterDetail = (characterId: string) => {
    const lifeTreeId = nodeDetail.lifeTreeId;
    if (lifeTreeId && characterId) {
      const path = `/lifetrees/${lifeTreeId}/characters/${characterId}`;
      window.open(path, '_blank');
    }
  };

  const handleCharacterStateDetail = (characterId: string, stateId: string) => {
    const stateData = nodeDetail.characterStates.find(cs => cs.id === stateId);
    if (!stateData) return;
    const character = lifetreeDetail?.characters.find(c => c.id === characterId);
    setStateDetailModal({
      visible: true,
      characterName: character?.name || '',
      state: stateData,
    });
  };

  const getRelationshipInfo = () => {
    if (!selectedNPCForRelationship || !mainCharacter || !mainCharacterStateInNode) return null;

    const mainCharId = mainCharacter.id;
    const mainStateId = mainCharacterStateInNode.stateId;
    const npcCharId = selectedNPCForRelationship.character.id;
    const npcStateId = selectedNPCForRelationship.state.id;

    const npcStateInNode = nodeDetail.characterStates.find(cs => cs.id === npcStateId);
    const mainStateInNode = nodeDetail.characterStates.find(cs => cs.id === mainStateId);

    const npcToMain = npcStateInNode?.relationships.find(r => r.targetId === mainCharId);
    const mainToNpc = mainStateInNode?.relationships.find(r => r.targetId === npcCharId);

    return { npcToMain, mainToNpc };
  };

  const relInfo = getRelationshipInfo();
  const npcToMain = relInfo?.npcToMain;
  const mainToNpc = relInfo?.mainToNpc;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Direction Section */}
      <Card 
        size="small" 
        style={{ 
          background: '#f0f5ff', 
          border: '1px solid #adc6ff',
        }}
        title={
          <Space>
            <RobotOutlined style={{ color: '#2f54eb' }} />
            <Text strong style={{ color: '#2f54eb' }}>Định hướng nội dung (Direction)</Text>
          </Space>
        }
        extra={
          <Space>
            {plan && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => setViewPlanModalOpen(true)}
                size="small"
              >
                Xem Plan
              </Button>
            )}
            <Button
              icon={<TeamOutlined />}
              onClick={onPlanCharacters}
              loading={planningCharacters}
              size="small"
            >
              Lập kế hoạch nhân vật (AI)
            </Button>
            <Button 
              type="text" 
              size="small" 
              icon={directionExpanded ? <UpOutlined /> : <DownOutlined />} 
              onClick={() => setDirectionExpanded(!directionExpanded)}
            />
          </Space>
        }
      >
        <div 
          onClick={() => setDirectionExpanded(!directionExpanded)}
          style={{ 
            padding: '8px 12px', 
            background: '#ffffff', 
            borderRadius: '4px',
            border: '1px solid #d6e4ff',
            maxHeight: directionExpanded ? '1000px' : 'none',
            overflowY: 'hidden',
            whiteSpace: 'pre-wrap',
            fontSize: '14px',
            lineHeight: '1.6',
            cursor: 'pointer',
            display: '-webkit-box',
            WebkitLineClamp: directionExpanded ? 'unset' : 3,
            WebkitBoxOrient: 'vertical',
            position: 'relative'
          }}
        >
          {direction || <Text type="secondary">Chưa có định hướng nội dung cho node này.</Text>}
        </div>
        
        {directionExpanded && (
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              AI sẽ dựa vào thông tin này để lập kế hoạch nhân vật và scenario.
            </Text>
          </div>
        )}
      </Card>

      {/* NPCs Title Section */}
      <div style={{ marginBottom: -8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>Nhân vật</Title>
        <Space>
          {plan && (
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={onDeployCharactersFromPlan}
              loading={deployingCharactersFromPlan}
              disabled={!plan}
            >
              Tạo nhân vật (AI)
            </Button>
          )}
          {plan && (
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={onDeployStatesFromPlan}
              loading={deployingStatesFromPlan}
              disabled={!plan}
            >
              Triển khai State (AI)
            </Button>
          )}
          {plan && (
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={onDeployRelationships}
              loading={deployingRelationships}
              disabled={!plan || nodeDetail.characterStates.length < 2}
            >
              Triển khai Quan hệ (AI)
            </Button>
          )}
          {nodeDetail.characterStates.length > 0 && (
            <Button
              type="primary"
              icon={<PictureOutlined />}
              onClick={onDeployImages}
              loading={deployingImages}
              disabled={nodeDetail.characterStates.length === 0}
            >
              Tạo ảnh State (AI)
            </Button>
          )}
          {plan && (
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={onDeployCharacters}
              loading={deployingCharacters || deployingFull}
              disabled={!plan}
            >
              Triển khai trọn gói (AI)
            </Button>
          )}
          {direction && !plan && !mainCharacterStateInNode && npcRows.every(row => !row.characterId) && (
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={onDeployCharacters}
              loading={deployingCharacters}
              disabled={!direction}
            >
              Triển khai nhân vật (AI)
            </Button>
          )}
          {direction && (mainCharacterStateInNode || npcRows.some(row => row.stateId)) && (
            <Popconfirm
              title="Xác nhận reset nhân vật?"
              description="Danh sách nhân vật sẽ bị gỡ bỏ khỏi node này để bạn có thể triển khai lại."
              onConfirm={onResetCharacters}
              okText="Xác nhận"
              cancelText="Hủy"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={resettingCharacters}
              >
                Reset nhân vật
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      {(deployingCharacters || deployingCharactersFromPlan || deployingStatesFromPlan || deployingRelationships || deployingImages || deployingFull) && deploymentProgress && (
        <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Đang triển khai nhân vật tự động...</Text>
            <Text type="secondary">{deploymentProgress.status}</Text>
            <div style={{ width: '100%', height: '8px', background: '#f5f5f5', borderRadius: '4px', overflow: 'hidden' }}>
              <div 
                style={{ 
                  width: `${deploymentProgress.total > 0 ? (deploymentProgress.current / deploymentProgress.total) * 100 : 0}%`, 
                  height: '100%', 
                  background: '#1890ff',
                  transition: 'width 0.3s ease'
                }} 
              />
            </div>
          </Space>
        </Card>
      )}

      {/* Main Character Section */}
      <Card 
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            <span>Nhân vật chính</span>
          </Space>
        }
        size="small"
      >
        {mainCharacterStateInNode ? (
          <Row gutter={16} style={{ alignItems: 'center', padding: '16px', background: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
            <Col span={7}>
              <Space>
                <Avatar 
                  src={mainCharacterStateInNode.characterAvatar} 
                  icon={<UserOutlined />}
                  size="small"
                />
                <div>
                  <Text strong>{mainCharacterStateInNode.characterName}</Text>
                  <Tag color="gold" style={{ marginLeft: 4, fontSize: '10px', lineHeight: '14px', height: '16px' }}>MAIN</Tag>
                </div>
              </Space>
            </Col>
            <Col span={15}>
              <div style={{ 
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Select
                  placeholder="Chọn state"
                  style={{ flex: 1 }}
                  value={mainCharacterStateInNode.stateId}
                  onChange={(value) => {
                    if (value === 'CREATE_NEW_STATE') {
                      onSetSelectedCharacterForState(mainCharacterStateInNode.characterId);
                      onSetMethodSelectionModalOpen(true);
                    } else {
                      onAddCharacterState(mainCharacterStateInNode.characterId, value);
                    }
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                  }
                  optionLabelProp="label"
                  variant="borderless"
                >
                  {getAvailableStatesForCharacter(mainCharacterStateInNode.characterId, mainCharacterStateInNode.stateId).map(state => (
                    <Option key={state.id} value={state.id} label={`${state.name} (Tuổi ${state.age})`}>
                      <Space>
                        <span>{state.name}</span>
                        <Text type="secondary">- Tuổi {state.age}</Text>
                        <Tag color={state.status === 'active' ? 'green' : 'orange'} style={{ fontSize: '10px' }}>
                          {state.status.toUpperCase()}
                        </Tag>
                      </Space>
                    </Option>
                  ))}
                  <Option key="CREATE_NEW_STATE" value="CREATE_NEW_STATE" label="Tạo state mới">
                    <Space>
                      <PlusOutlined style={{ color: '#1890ff' }} />
                      <Text style={{ color: '#1890ff' }}>Tạo state mới</Text>
                    </Space>
                  </Option>
                </Select>

                <Space>
                  <Tag color={
                    nodeDetail.characterStates.find(cs => cs.id === mainCharacterStateInNode.stateId)?.status === 'active' 
                      ? 'green' 
                      : 'orange'
                  } style={{ margin: 0, fontSize: '10px' }}>
                    {nodeDetail.characterStates.find(cs => cs.id === mainCharacterStateInNode.stateId)?.status?.toUpperCase()}
                  </Tag>
                  {nodeDetail.characterStates.find(cs => cs.id === mainCharacterStateInNode.stateId)?.status === 'draft' && (
                    <Button
                      size="small"
                      type="primary"
                      loading={activatingStates.has(mainCharacterStateInNode.stateId)}
                      onClick={() => onActivateState(mainCharacterStateInNode.characterId, mainCharacterStateInNode.stateId)}
                      style={{ fontSize: '11px', height: '22px' }}
                    >
                      Kích hoạt
                    </Button>
                  )}
                </Space>
              </div>
            </Col>
            <Col span={2} style={{ textAlign: 'right' }}>
              <Space>
                <Button 
                  type="text" 
                  size="small" 
                  icon={<EyeOutlined />} 
                  onClick={() => handleCharacterDetail(mainCharacterStateInNode.characterId)}
                  title="Xem thông tin nhân vật"
                />
                <Button 
                  type="text" 
                  size="small" 
                  icon={<IdcardOutlined />} 
                  onClick={() => handleCharacterStateDetail(mainCharacterStateInNode.characterId, mainCharacterStateInNode.stateId)}
                  title="Xem thông tin state"
                />
                <Popconfirm
                  title="Xóa nhân vật chính khỏi node?"
                  onConfirm={() => onRemoveCharacterState(mainCharacterStateInNode.stateId)}
                  okText="Có"
                  cancelText="Không"
                >
                  <Button 
                    icon={<DeleteOutlined />} 
                    size="small" 
                    danger
                    type="text"
                  />
                </Popconfirm>
              </Space>
            </Col>
          </Row>
        ) : mainCharacter ? (
          <Row gutter={16} style={{ alignItems: 'center', padding: '16px', background: '#fafafa', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
            <Col span={8}>
              <Space>
                <Avatar 
                  src={mainCharacter.imageUrl} 
                  icon={<UserOutlined />}
                  size="small"
                />
                <div>
                  <Text strong>{mainCharacter.name}</Text>
                  <Tag color="gold" style={{ marginLeft: 4 }}>MAIN</Tag>
                </div>
              </Space>
            </Col>
            <Col span={14}>
              <Select
                placeholder="Chọn state"
                style={{ width: '100%' }}
                onSelect={(value: string) => {
                  if (value === 'CREATE_NEW_STATE') {
                    onSetSelectedCharacterForState(mainCharacter.id);
                    onSetMethodSelectionModalOpen(true);
                  } else {
                    onAddCharacterState(mainCharacter.id, value);
                  }
                }}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                }
                optionLabelProp="label"
              >
                {getAvailableStatesForCharacter(mainCharacter.id, null).map(state => (
                  <Option key={state.id} value={state.id} label={`${state.name} (Tuổi ${state.age})`}>
                    <Space>
                      <span>{state.name}</span>
                      <Text type="secondary">- Tuổi {state.age}</Text>
                      <Tag color={state.status === 'active' ? 'green' : 'orange'} style={{ fontSize: '10px' }}>
                        {state.status.toUpperCase()}
                      </Tag>
                    </Space>
                  </Option>
                ))}
                <Option key="CREATE_NEW_STATE" value="CREATE_NEW_STATE" label="Tạo state mới">
                  <Space>
                    <PlusOutlined style={{ color: '#1890ff' }} />
                    <Text style={{ color: '#1890ff' }}>Tạo state mới</Text>
                  </Space>
                </Option>
              </Select>
            </Col>
            <Col span={2}>
              <Button 
                type="text" 
                size="small" 
                icon={<EyeOutlined />} 
                onClick={() => handleCharacterDetail(mainCharacter.id)}
                title="Xem thông tin nhân vật"
              />
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px' }}>
            <CrownOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Chưa có nhân vật chính trong LifeTree này</Text>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={onShowCreateCharacterModal}
            >
              Tạo nhân vật chính
            </Button>
          </div>
        )}
      </Card>

      {/* NPCs Section */}
      <Card 
        title={
          <Space>
            <TeamOutlined style={{ color: '#1890ff' }} />
            <span>Nhân vật phụ (NPCs)</span>
          </Space>
        }
        size="small"
        extra={
          <Button 
            icon={<PlusOutlined />}
            onClick={onShowCreateCharacterModal}
          >
            Tạo NPC mới
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Chọn nhân vật NPC và state tương ứng để thêm vào node này
          </Text>
        </div>
        
        {npcRows.map((row) => {
          const character = row.characterId ? lifetreeDetail?.characters.find(c => c.id === row.characterId) : null;
          const state = row.stateId ? character?.states.find(s => s.id === row.stateId) : null;
          const availableStates = row.characterId ? getAvailableStatesForCharacter(row.characterId, row.stateId) : [];
          const actualStateStatus = row.stateId ? nodeDetail.characterStates.find(cs => cs.id === row.stateId)?.status || state?.status : null;
          
          return (
            <Row key={row.id} gutter={16} style={{ marginBottom: 12, alignItems: 'center' }}>
              <Col span={7}>
                {character ? (
                  <Space>
                    <Avatar 
                      src={character.imageUrl} 
                      icon={<UserOutlined />}
                      size="small"
                    />
                    <div>
                      <div style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Text strong title={character.name}>{character.name}</Text>
                      </div>
                      <Tag color="blue" style={{ fontSize: '10px', lineHeight: '14px', height: '16px' }}>NPC</Tag>
                    </div>
                  </Space>
                ) : (
                  <Select
                    placeholder="Chọn NPC"
                    style={{ width: '100%' }}
                    value={row.characterId}
                    onChange={(value) => onNPCCharacterChange(row.id, value)}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                    }
                    optionLabelProp="label"
                  >
                    {getAvailableNPCCharacters(row.id).map(char => (
                      <Option key={char.id} value={char.id} label={char.name}>
                        <Space>
                          <Avatar src={char.imageUrl} icon={<UserOutlined />} size="small" />
                          <span>{char.name}</span>
                          <Tag color="blue" style={{ marginLeft: 4 }}>NPC</Tag>
                        </Space>
                      </Option>
                    ))}
                    <Option key="CREATE_NEW" value="CREATE_NEW" label="Tạo NPC mới">
                      <Space>
                        <PlusOutlined style={{ color: '#1890ff' }} />
                        <Text style={{ color: '#1890ff' }}>Tạo NPC mới</Text>
                      </Space>
                    </Option>
                  </Select>
                )}
              </Col>
              
              <Col span={15}>
                {row.characterId ? (
                  <div style={{ 
                    width: '100%',
                    padding: '4px 8px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    background: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Select
                      placeholder="Chọn state"
                      style={{ flex: 1 }}
                      value={row.stateId}
                      onChange={(value) => {
                        if (value === 'CREATE_NEW_STATE') {
                          onNPCStateChange(row.id, null as any); // Reset current
                          onSetSelectedCharacterForState(row.characterId!);
                          onSetMethodSelectionModalOpen(true);
                        } else {
                          onNPCStateChange(row.id, value);
                        }
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                      }
                      optionLabelProp="label"
                      variant="borderless"
                    >
                      {availableStates.map(st => (
                        <Option key={st.id} value={st.id} label={`${st.name} (Tuổi ${st.age})`}>
                          <Space>
                            <span>{st.name}</span>
                            <Text type="secondary">- Tuổi {st.age}</Text>
                            <Tag color={st.status === 'active' ? 'green' : 'orange'} style={{ fontSize: '10px' }}>
                              {st.status.toUpperCase()}
                            </Tag>
                          </Space>
                        </Option>
                      ))}
                      <Option key="CREATE_NEW_STATE" value="CREATE_NEW_STATE" label="Tạo state mới">
                        <Space>
                          <PlusOutlined style={{ color: '#1890ff' }} />
                          <Text style={{ color: '#1890ff' }}>Tạo state mới</Text>
                        </Space>
                      </Option>
                    </Select>

                    {row.stateId && (
                      <Space>
                        <Tag color={actualStateStatus === 'active' ? 'green' : 'orange'} style={{ margin: 0, fontSize: '10px' }}>
                          {actualStateStatus?.toUpperCase()}
                        </Tag>
                        {actualStateStatus === 'draft' && (
                          <Button
                            size="small"
                            type="primary"
                            loading={activatingStates.has(row.stateId)}
                            onClick={() => onActivateState(row.characterId!, row.stateId!)}
                            style={{ fontSize: '11px', height: '22px' }}
                          >
                            Kích hoạt
                          </Button>
                        )}
                      </Space>
                    )}
                  </div>
                ) : (
                  <div style={{ height: '32px' }} /> // Placeholder to keep layout
                )}
              </Col>
              
              <Col span={2} style={{ textAlign: 'right' }}>
                <Space>
                  {row.characterId && (
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<EyeOutlined />} 
                      onClick={() => handleCharacterDetail(row.characterId!)}
                      title="Xem thông tin nhân vật"
                    />
                  )}
                  {row.characterId && row.stateId && (
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<IdcardOutlined />} 
                      onClick={() => handleCharacterStateDetail(row.characterId!, row.stateId!)}
                      title="Xem thông tin state"
                    />
                  )}
                  {row.characterId && row.stateId && character && state && (
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<HeartOutlined />} 
                      onClick={() => showRelationshipModal(character, state)}
                      style={{ color: '#ff4d4f' }}
                      title="Xem mối quan hệ"
                    />
                  )}
                  <Popconfirm
                    title="Xóa NPC này khỏi node?"
                    onConfirm={() => onRemoveNPCRow(row.id)}
                    okText="Có"
                    cancelText="Không"
                    disabled={!row.characterId && !row.stateId}
                  >
                    <Button 
                      icon={<DeleteOutlined />} 
                      size="small" 
                      danger
                      type="text"
                      disabled={!row.characterId && !row.stateId && npcRows.length === 1}
                    />
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          );
        })}
        
        {npcRows.every(row => row.stateId === null) && (
          <Empty 
            image={<TeamOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />}
            description="Chưa có nhân vật NPC nào trong node này"
            style={{ margin: '32px 0' }}
          />
        )}
      </Card>

      {/* Relationship Modal */}
      <Modal
        title={
          <Space>
            <HeartOutlined style={{ color: '#ff4d4f' }} />
            <span>Mối quan hệ: {mainCharacter?.name} & {selectedNPCForRelationship?.character.name}</span>
          </Space>
        }
        open={relationshipModalVisible}
        onCancel={() => setRelationshipModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRelationshipModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px', margin: '-12px -24px', padding: '12px 24px' }}>
          {!mainCharacterStateInNode ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text type="secondary">Vui lòng chọn state cho nhân vật chính để xem mối quan hệ.</Text>
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {/* NPC to Main Character */}
              <Card size="small" title={`Góc nhìn của ${selectedNPCForRelationship?.character.name}`} headStyle={{ background: '#f0f5ff' }}>
                {npcToMain ? (
                  <Descriptions column={2} size="small" layout="vertical" bordered>
                    <Descriptions.Item label="Thiết lập danh hiệu">{npcToMain.initTitle}</Descriptions.Item>
                    <Descriptions.Item label="Thiết lập điểm">{npcToMain.initScore}</Descriptions.Item>
                    <Descriptions.Item label="Danh hiệu hiện tại" span={1}>
                      <Tag color="blue">{npcToMain.currentTitle}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Điểm hiện tại" span={1}>
                      <Text strong style={{ color: '#1890ff' }}>{npcToMain.currentScore}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Ý định/Cảm xúc" span={2}>
                      <div style={{ padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                        {npcToMain.intent}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Chi tiết mối quan hệ" span={2}>
                      <div style={{ padding: '8px', background: '#f9f9f9', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                        {npcToMain.detail || <Text type="secondary" italic>Chưa có mô tả chi tiết.</Text>}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <InfoCircleOutlined style={{ color: '#d9d9d9', fontSize: '24px', marginBottom: 8 }} />
                    <div><Text type="secondary">Chưa có thông tin mối quan hệ từ {selectedNPCForRelationship?.character.name} đến {mainCharacter?.name}</Text></div>
                  </div>
                )}
              </Card>

              <Divider style={{ margin: '8px 0' }} />

              {/* Main Character to NPC */}
              <Card size="small" title={`Góc nhìn của ${mainCharacter?.name}`} headStyle={{ background: '#fff7e6' }}>
                {mainToNpc ? (
                  <Descriptions column={2} size="small" layout="vertical" bordered>
                    <Descriptions.Item label="Thiết lập danh hiệu">{mainToNpc.initTitle}</Descriptions.Item>
                    <Descriptions.Item label="Thiết lập điểm">{mainToNpc.initScore}</Descriptions.Item>
                    <Descriptions.Item label="Danh hiệu hiện tại" span={1}>
                      <Tag color="orange">{mainToNpc.currentTitle}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Điểm hiện tại" span={1}>
                      <Text strong style={{ color: '#faad14' }}>{mainToNpc.currentScore}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Ý định/Cảm xúc" span={2}>
                      <div style={{ padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                        {mainToNpc.intent}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="Chi tiết mối quan hệ" span={2}>
                      <div style={{ padding: '8px', background: '#f9f9f9', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                        {mainToNpc.detail || <Text type="secondary" italic>Chưa có mô tả chi tiết.</Text>}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <InfoCircleOutlined style={{ color: '#d9d9d9', fontSize: '24px', marginBottom: 8 }} />
                    <div><Text type="secondary">Chưa có thông tin mối quan hệ từ {mainCharacter?.name} đến {selectedNPCForRelationship?.character.name}</Text></div>
                  </div>
                )}
              </Card>
            </Space>
          )}
        </div>
      </Modal>

      {/* State Detail Modal */}
      <Modal
        title={
          <Space>
            <IdcardOutlined style={{ color: '#1890ff' }} />
            <span>Chi tiết State — {stateDetailModal.characterName}</span>
          </Space>
        }
        open={stateDetailModal.visible}
        onCancel={() => setStateDetailModal({ visible: false, characterName: '', state: null })}
        footer={[
          <Button key="close" onClick={() => setStateDetailModal({ visible: false, characterName: '', state: null })}>
            Đóng
          </Button>
        ]}
        width={800}
      >
        {stateDetailModal.state && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {/* Avatar + basic info */}
              <Row gutter={16} align="middle">
                <Col flex="none">
                  <Avatar
                    src={stateDetailModal.state.imageUrl}
                    icon={<UserOutlined />}
                    size={80}
                    style={{ border: '2px solid #d9d9d9' }}
                  />
                </Col>
                <Col flex="auto">
                  <Space direction="vertical" size={2}>
                    <Space align="center">
                      <Text strong style={{ fontSize: '18px' }}>{stateDetailModal.state.name}</Text>
                      <Tag color={stateDetailModal.state.status === 'active' ? 'green' : 'orange'}>
                        {stateDetailModal.state.status?.toUpperCase()}
                      </Tag>
                    </Space>
                    {stateDetailModal.state.alias && (
                      <Text type="secondary">Biệt danh: {stateDetailModal.state.alias}</Text>
                    )}
                    <Text type="secondary">Tuổi: {stateDetailModal.state.age}</Text>
                    {stateDetailModal.state.occupation && (
                      <Text type="secondary">Nghề nghiệp: {stateDetailModal.state.occupation}</Text>
                    )}
                  </Space>
                </Col>
              </Row>

              {stateDetailModal.state.description && (
                <Card size="small" title="Mô tả nội bộ">
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{stateDetailModal.state.description}</Text>
                </Card>
              )}

              {stateDetailModal.state.displayedDescription && (
                <Card size="small" title="Mô tả hiển thị">
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{stateDetailModal.state.displayedDescription}</Text>
                </Card>
              )}

              {stateDetailModal.state.appearance?.length > 0 && (
                <Card size="small" title="Ngoại hình">
                  <Descriptions column={1} size="small" bordered>
                    {stateDetailModal.state.appearance.map((item: any, idx: number) => (
                      <Descriptions.Item key={idx} label={item.key}>{item.value}</Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              )}

              {stateDetailModal.state.innerSelf?.length > 0 && (
                <Card size="small" title="Tính cách / Nội tâm">
                  <Descriptions column={1} size="small" bordered>
                    {stateDetailModal.state.innerSelf.map((item: any, idx: number) => (
                      <Descriptions.Item key={idx} label={item.key}>{item.value}</Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              )}

              {stateDetailModal.state.relationshipStyles?.length > 0 && (
                <Card size="small" title="Phong cách quan hệ">
                  <Descriptions column={1} size="small" bordered>
                    {stateDetailModal.state.relationshipStyles.map((item: any, idx: number) => (
                      <Descriptions.Item key={idx} label={item.key}>{item.value}</Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              )}

              {stateDetailModal.state.images?.length > 0 && (
                <Card size="small" title="Hình ảnh">
                  <Space wrap>
                    {stateDetailModal.state.images.map((img: any) => (
                      <Image
                        key={img.id}
                        src={img.imageUrl}
                        width={100}
                        height={100}
                        style={{ objectFit: 'cover', borderRadius: '4px' }}
                      />
                    ))}
                  </Space>
                </Card>
              )}
            </Space>
          </div>
        )}
      </Modal>

      {/* View Plan Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>Kế hoạch chi tiết (JSON Plan)</span>
          </Space>
        }
        open={viewPlanModalOpen}
        onCancel={() => setViewPlanModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setViewPlanModalOpen(false)}>
            Đóng
          </Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <pre style={{ 
            background: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '4px', 
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}>
            {JSON.stringify(plan, null, 2)}
          </pre>
        </div>
      </Modal>
    </Space>
  );
};

export default NodeCharactersTab;
